import { MessageBarType, Shimmer, ShimmerElementType } from '@fluentui/react';
import { Button, buttonStylesDanger, Checkbox, MessageBar, useTheme } from '@h2oai/ui-kit';
import { useEffect, useState } from 'react';

import { useAdminCenterService } from '../../../../../admin-center/hooks';
import { useUser } from '../../../../../utils/hooks';
import { buildUserContext } from '../../../common/utils';
import {
  groupItemStyles,
  groupListHeaderStyles,
  groupListRoot,
  itemsContainer,
  messageBarContainerStyles,
  shimmerItemStyles,
} from '../CustomGroupManagement.styles';

type UserGroupsListProps = {
  refreshKey: number;
  onMessage: (msg: string) => void;
};

export default function UserGroupsList({ refreshKey, onMessage }: UserGroupsListProps) {
  const service = useAdminCenterService();
  const [groups, setGroups] = useState<string[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const theme = useTheme();
  const user = useUser();

  // Fetch groups on mount
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        setLoading(true);
        const resp = await service.getCustomerManagedGroupsRequest({});
        setGroups(resp.userGroups || []);
      } catch (err) {
        setError('Failed to load groups');
      } finally {
        setLoading(false);
      }
    };
    fetchGroups();
  }, [service, refreshKey, user]);

  const toggleSelect = (group: string) => {
    setSelectedGroups((prev) => {
      const copy = new Set(prev);
      if (copy.has(group)) {
        copy.delete(group);
      } else {
        copy.add(group);
      }
      return copy;
    });
  };

  const handleDelete = async () => {
    if (selectedGroups.size === 0) return;
    try {
      const userContext = await buildUserContext(user);
      setLoading(true);
      const response = await service.removeUserGroupsRequest({
        members: Array.from(selectedGroups),
        userContext: userContext,
      });
      if (response.statusUpdate) {
        const statusMessage = response.statusUpdate;
        onMessage(statusMessage);
      } else {
        onMessage('Failed to update member group');
      }
      setGroups((prev) => prev.filter((g) => !selectedGroups.has(g)));
      setSelectedGroups(new Set());
    } catch (err) {
      setError('Error: Failed to delete groups');
    } finally {
      setLoading(false);
    }
  };

  const renderShimmerItems = () => {
    return Array.from({ length: 6 }, (_, index) => (
      <div key={`shimmer-${index}`} style={shimmerItemStyles(theme)}>
        <Shimmer
          shimmerElements={[
            { type: ShimmerElementType.circle, width: 20, height: 20 },
            { type: ShimmerElementType.gap, width: 12 },
            { type: ShimmerElementType.line, width: `${Math.random() * 100 + 80}px`, height: 16 },
          ]}
        />
      </div>
    ));
  };

  return (
    <div style={groupListRoot()}>
      {error && (
        <div style={messageBarContainerStyles()}>
          <MessageBar timeout={6000} onDismiss={() => setError(null)} messageBarType={MessageBarType.error}>
            {error}
          </MessageBar>
        </div>
      )}

      <div style={groupListHeaderStyles()}>
        <div>
          <h2 style={{ margin: 0, color: theme.palette?.gray750 }}>Configured Groups</h2>
          <p style={{ color: theme.palette?.gray500, margin: '8px 0 0 0', fontSize: '14px' }}>
            Select groups to delete them.
          </p>
        </div>
        <Button
          text={loading ? 'Deleting...' : `Delete Selected (${selectedGroups.size})`}
          onClick={handleDelete}
          disabled={selectedGroups.size === 0 || loading}
          styles={buttonStylesDanger}
        />
      </div>

      <div style={itemsContainer()}>
        {loading ? (
          renderShimmerItems()
        ) : groups.length === 0 ? (
          <p>No user groups found. Create your first group above.</p>
        ) : (
          groups.map((group) => (
            <div key={group} style={groupItemStyles(theme)} onClick={() => toggleSelect(group)}>
              <Checkbox
                checked={selectedGroups.has(group)}
                onChange={() => toggleSelect(group)}
                styles={{
                  root: { marginRight: '12px' },
                }}
              />
              <span
                style={{
                  fontSize: '15px',
                  fontWeight: selectedGroups.has(group) ? '600' : '400',
                }}
              >
                {group}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
